// Single source of truth for the task category set on web — mirrors the mobile
// app's src/constants/categories.ts so the color taxonomy is identical across
// platforms. Every web consumer (task cards, future Deals/Travel UIs, the
// Change Category control) should import from here.
//
// Colors resolve to the CSS custom properties declared in src/styles/tokens.css
// (--cat-*), so they automatically stay in sync with the design tokens and read
// correctly over both light and dark surfaces. `colorHex` is kept for the rare
// case a raw value is needed (e.g. canvas, inline SVG fill).
//
// Order matters: it drives section order in the task list (Shopping first,
// Other last). Keep it stable.

export const CATEGORIES = [
  { name: 'Shopping', icon: 'shopping-cart', colorHex: '#FF6B6B', token: 'shopping' },
  { name: 'Travel',   icon: 'plane',         colorHex: '#0D9488', token: 'travel' },
  { name: 'Work',     icon: 'briefcase',     colorHex: '#3B82F6', token: 'work' },
  { name: 'Health',   icon: 'activity',      colorHex: '#22C55E', token: 'health' },
  { name: 'Money',    icon: 'dollar-sign',   colorHex: '#6366F1', token: 'money' },
  { name: 'Personal', icon: 'users',         colorHex: '#EC4899', token: 'personal' },
  { name: 'Other',    icon: 'list',          colorHex: '#F59E0B', token: 'other' },
];

// Pseudo-categories surfaced in some views (routines tab, shared/group lanes)
// without being real Task buckets. Kept separate from CATEGORIES so they don't
// appear in the task category picker.
const PSEUDO = {
  routine: { name: 'Routine', icon: 'repeat', colorHex: '#8B5CF6', token: 'routine' },
  group:   { name: 'Group',   icon: 'users',  colorHex: '#0EA5E9', token: 'group' },
};

export const CATEGORY_NAMES = CATEGORIES.map((c) => c.name);

const META_BY_NAME = new Map(
  [...CATEGORIES, ...Object.values(PSEUDO)].map((c) => [c.name.toLowerCase(), c]),
);

const OTHER = CATEGORIES[CATEGORIES.length - 1];

// Lookup by name (case-insensitive — backend stores capitalized, but legacy
// rows or external callers may use any casing). Falls back to Other.
export function getCategoryMeta(category) {
  if (!category) return OTHER;
  return META_BY_NAME.get(String(category).toLowerCase()) ?? OTHER;
}

// Solid accent — filled icons, focused text, badges. Returns a CSS var() so the
// value tracks the active theme's tokens.
export function getCategoryColor(category) {
  return `var(--cat-${getCategoryMeta(category).token})`;
}

// Soft tinted card background per category — for the colorful task cards.
export function getCategoryCardBg(category) {
  return `var(--cat-${getCategoryMeta(category).token}-bg)`;
}

// Companion soft border — same hue, slightly more present than the card bg.
export function getCategoryCardBorder(category) {
  return `var(--cat-${getCategoryMeta(category).token}-border)`;
}

// Raw hex accent — only when a CSS var can't be used (canvas, inline SVG fill).
export function getCategoryColorHex(category) {
  return getCategoryMeta(category).colorHex;
}
